import format from 'date-fns/format';
import isFuture from 'date-fns/isFuture';

import { getOnlineStatus } from '@/modules/car/api';
import { ICreateDriverForm } from '@/pages/ChauffeurCreate/types';
import { createFormData } from '@/shared/api/create-form-data';
import { get, post, put } from '@/shared/api/fetch-based-http-methods';
import { prepareDate, prepareDateForRequest } from '@/shared/api/prepare-date';
import { CountryCode, TCountryCodes } from '@/shared/models/country-code';
import { EntityPage } from '@/shared/models/entity-page';
import { gendersEnum, TGender } from '@/shared/models/gender';
import {
  licensingAuthorityTypesEnum,
  TLicensingAuthorityType,
} from '@/shared/models/licensingAuthority';
import { UploadResult } from '@/shared/models/upload-result';
import { prepareUploadedFile, UploadedFile } from '@/shared/models/uploaded-file';
import { IDocUploaderForm } from '@/shared/store/doc-uploader/types';
import { formatAddress } from '@/shared/utils/formatAddress';
import { formatDate } from '@/shared/utils/formatDate';
import { getApiUrl } from '@/shared/utils/getApiUrl';
import { getPhoneFromCodeAndNumber } from '@/shared/utils/phoneUtils';
import { skipNullishDeep } from '@/shared/utils/skip-nullish-deep';
import { w4dDriversRemoteAPI } from '@src-common/api/definitions/w4d-drivers-endpoints';
import {
  TNewDriverResponse,
  TDriverBrief as TRawDriverBrief,
  TDriverDetails,
  TRawDriverWorkload,
} from '@src-common/api/definitions/w4d-drivers-types';
import { createClient } from '@src-common/api/src/create-client';

import type { IDriverBrief } from '../model';
import { TDriverForm } from '../model';
import { IDriver, TransferRequest } from '../model/driver';
import { DriverDocType, DriverDocDefinition } from '../model/driver-doc-type';
import { IDriverWorkload, IWorkloadData } from '../model/driver-workload';
import { IReferralCode } from '../model/referral-code';

const client = createClient(w4dDriversRemoteAPI({ get, post, put, apiHost: getApiUrl() }));

const prepareTransferRequest = (
  rawTransferRequest: TDriverDetails['driver_transfer_request'],
): TransferRequest | undefined =>
  rawTransferRequest
    ? {
        status: rawTransferRequest.status,
        leavingAtDate: new Date(rawTransferRequest.left_at),
      }
    : undefined;

const prepareLicenseNumber = (rawLicenseNumber?: string) => {
  if (!rawLicenseNumber || rawLicenseNumber === 'XXXXXX') {
    return '';
  }

  return rawLicenseNumber;
};

const prepareChauffeurOperator = (
  chauffeurOperator?: boolean,
): 'wheely_tech' | 'wheely_london' | undefined => {
  if (typeof chauffeurOperator === 'boolean') {
    return chauffeurOperator ? 'wheely_tech' : 'wheely_london';
  }

  return;
};

const prepareDriver = (driver: TRawDriverBrief): IDriverBrief => ({
  homeAddress: driver.home_address || '',
  birthday: prepareDate(driver.birthday),
  gender: driver.gender && driver.gender in gendersEnum ? (driver.gender as TGender) : null,
  ukNationalInsurance: driver.uk_national_insurance || '',
  licenseNumber: prepareLicenseNumber(driver.license_number),
  email: driver.email || '',
  firstName: driver.first_name,
  id: driver.id,
  lastName: driver.last_name,
  phone: driver.phone,
  rating: driver.rating,
  score: driver.score,
  testStatuses: driver.tests
    ? Object.fromEntries(driver.tests.map(({ test_id, status }) => [test_id, status]))
    : {},
  // TODO remove ?? 'active' when BE will support this field
  accessStatus: driver.access_status ?? 'active',
  docUploads: Object.fromEntries<UploadedFile<DriverDocType>>(
    driver.uploads?.map(rawUpload => [
      rawUpload.type,
      prepareUploadedFile<DriverDocType>(rawUpload),
    ]) ?? [],
  ),
  photoURL: (driver.photo || driver.photo_url) ?? undefined,
  licensingAuthority:
    driver.license_authority && driver.license_authority in licensingAuthorityTypesEnum
      ? (driver.license_authority as TLicensingAuthorityType)
      : null,
  lastActivity: driver.last_activity_at ? new Date(driver.last_activity_at) : undefined,
  carId: driver.car?.id,
  onlineStatus:
    driver.car?.current_driver_id === driver.id
      ? getOnlineStatus(driver.car?.status_hash || {})
      : 'offline',
  hasPassedAccreditation: Boolean(driver.passed_accreditation),
  chauffeurOperator: prepareChauffeurOperator(driver.chauffeur_operator),
});

const prepareDriverDetails = (raw: TDriverDetails): IDriver => ({
  ...prepareDriver(raw),
  partnerId: raw.partner?.id,
  transferRequest: prepareTransferRequest(raw.driver_transfer_request),
  hasContractWithPartner: raw.partner_driver_contract === 'approved',
  tags: raw.tags,
  acceptance: raw.acceptance,
  cityId: raw.city_id,
  blockInfo: raw.block_info
    ? {
        blockedBy: raw.block_info.blocked_by,
        blockedAt: raw.block_info.blocked_at ? new Date(raw.block_info.blocked_at) : null,
        notes: raw.block_info.notes,
        reason: raw.block_info.reason,
      }
    : undefined,
});

const prepareDriverUpdate = (update: TNewDriverResponse): Partial<IDriverBrief> => ({
  phone: update.phone,
  id: update.id,
});

const prepareWorkload = (rawWorkload: TRawDriverWorkload): IDriverWorkload => {
  const timeMap = rawWorkload.intervals.reduce(
    (hash, interval) => {
      const date = new Date(interval.ts);
      const time = format(date, 'HH:mm');
      const minutesOnline = Math.ceil(interval.online_minutes);

      const workloadData: IWorkloadData = {
        ts: interval.ts,
        minutesOnline,
        isFuture: isFuture(date),
      };

      if (time in hash) {
        hash[time].push(workloadData);
      } else {
        hash[time] = [workloadData];
      }

      return hash;
    },
    {} as Record<string, IWorkloadData[]>,
  );

  return {
    hourlyWorkloads: timeMap,
    summary: {
      totalMinutes: rawWorkload.summary.online_minutes,
      amount: rawWorkload.summary.price,
    },
  };
};

const driverAPI = {
  sendTransferRequest: async (driverId: string) => {
    const response = await client.sendTransferRequest({
      pathParams: { driverId },
    });

    return prepareTransferRequest(response.driver_transfer_request);
  },
  fetchReferralCode: async (): Promise<IReferralCode> => {
    const response = await client.readReferral();

    return response.referral_info;
  },
  changeContractStatus: async (
    driverId: string,
    isActive: boolean,
  ): Promise<IDriver | undefined> => {
    const response = await client.changeContractStatus({
      pathParams: { driverId },
      payload: { status: isActive ? 'approved' : 'rejected' },
    });

    if (response.driver) {
      return prepareDriverDetails(response.driver);
    }

    return undefined;
  },
  blockDriver: async (
    driverId: string,
    reason: string,
    message: string,
  ): Promise<IDriver | undefined> => {
    const response = await client.blockDriver({
      pathParams: { id: driverId },
      payload: { blocked_reason: reason, blocked_reason_notes: message },
    });

    if (response.driver) {
      return prepareDriverDetails(response.driver);
    }
  },
  fetchMyDriver: async (): Promise<IDriver> => {
    const response = await client.readMyDriver();

    return prepareDriverDetails(response.driver);
  },
  fetchBriefDrivers: async ({
    limit = 100,
    offset = 0,
    query = '',
  }: {
    limit?: number;
    offset?: number;
    query?: string;
  } = {}): Promise<EntityPage<IDriver>> => {
    const response = await client.readDrivers({ pathParams: { limit, skip: offset, query } });
    const items = response.results.map(prepareDriverDetails);

    return {
      items,
      total: response.total_entries,
      limit: response.limit,
      offset: response.skip,
    };
  },
  fetchDriverById: async (id: string): Promise<IDriver> => {
    const response = await client.readDriverDetails({ pathParams: { id } });

    return prepareDriverDetails(response.driver);
  },
  createDriver: async (
    driverForm: ICreateDriverForm,
    countryCode: TCountryCodes,
  ): Promise<[IDriver?, Partial<Record<keyof IDriver, string[]>>?]> => {
    const response = await client.createDriver({
      payload: {
        email: driverForm.email,
        first_name: driverForm.firstName,
        home_address: formatAddress(driverForm),
        last_name: driverForm.lastName,
        license_number: driverForm.licenseNumber,
        phone: getPhoneFromCodeAndNumber({
          code: driverForm.phoneCode,
          number: driverForm.phoneNumber,
        }),
        uk_national_insurance: driverForm.ukNationalInsurance,
        license_authority: driverForm.licensingAuthority,
        middle_name: null,
        photo: null,
        birthday: prepareDateForRequest(driverForm.birthday),
        rating: null,
        created_at: '',
        score: null,
        acceptance: null,
        access_status: 'pending',
        last_activity_at: null,
        car_id: null,
        block_info: null,
        partner_driver_contract: countryCode === CountryCode.Russia ? 'approved' : null,
        passed_tests: [],
      },
    });

    if ('driver' in response) {
      return [prepareDriverUpdate(response.driver) as IDriver, undefined];
    }

    if ('errors' in response) {
      const validation = skipNullishDeep(
        {
          phoneNumber: response.errors.phone,
          email: response.errors.email,
        },
        false,
      );

      const userExistsErrorMessage = response.errors.user_exists;

      if (userExistsErrorMessage) {
        if (userExistsErrorMessage.includes('email') && !response.errors.email) {
          validation.email = userExistsErrorMessage;
        } else if (!response.errors.phone) {
          validation.phoneNumber = userExistsErrorMessage;
        }
      }

      return [undefined, validation];
    }

    return [];
  },
  updateDriver: async (
    driverId: string,
    update: Partial<TDriverForm>,
  ): Promise<[boolean, Partial<Record<keyof IDriver, string[]>>?]> => {
    const response = await client.updateDriver({
      payload: {
        email: update.email,
        first_name: update.firstName,
        home_address: update.homeAddress,
        last_name: update.lastName,
        license_number: update.licenseNumber,
        phone: update.phone,
        uk_national_insurance: update.ukNationalInsurance,
        license_authority: update.licensingAuthority,
        car_id: update.carId,
      },
      pathParams: { id: driverId },
    });

    if (response.driver) {
      return [true];
    }

    const errors = { ...response.errors };
    const { license_authority, license_number, home_address, user_exists } = response.errors;

    if (license_authority) {
      errors.licensingAuthority = license_authority;
      delete errors.license_authority;
    }

    if (license_number) {
      errors.licenseNumber = license_number;
      delete errors.license_number;
    }

    if (home_address) {
      errors.homeAddress = home_address;
      delete errors.home_address;
    }

    if (user_exists) {
      errors['phone'] = user_exists;
      delete errors.user_exists;
    }

    return [false, errors];
  },
  fetchDocDefinitions: async (): Promise<Array<DriverDocDefinition>> => {
    const response = await client.readDriverDocumentDefinitions();

    return response.map(raw => ({
      docType: raw.type,
      title: raw.caption,
      comment: raw.comment || undefined,
      desc: raw.description || undefined,
      exampleUrl: raw.example_url || undefined,
      isOptional: raw.optional,
      isExpires: raw.expires || false,
    }));
  },
  uploadFile: async (
    form: IDocUploaderForm<DriverDocType>,
    holderId: string,
  ): Promise<UploadResult<DriverDocType>> => {
    const response = await client.uploadDriverDocument({
      pathParams: { driverId: holderId },
      payload: createFormData(
        skipNullishDeep(
          {
            type: form.docType,
            expires_at: form.expiresAt && formatDate(form.expiresAt), // YYYY-MM-DD
            upload_file: form.file?.file,
          },
          false,
        ),
      ),
    });

    if (!('upload' in response)) {
      if ('errors' in response) {
        if (response.errors.file) {
          return {
            error: response.errors.file[0],
          };
        }
      }
    }

    if ('upload' in response) {
      return {
        id: response.upload.id,
        type: response.upload.type,
      };
    }

    return {
      error: '',
    };
  },
  loadWorkload: async ({ driverId, from, to }: { driverId: string; from: Date; to: Date }) => {
    const response = await client.loadWorkload({
      pathParams: { driverId, from: from.toISOString(), to: to.toISOString() },
    });

    return prepareWorkload(response);
  },
};

export { driverAPI };
